import { store } from "#puf/store/index.ts";
import { z } from "zod";

/**
 * Catalog of guard predicates referenceable from user-built state machines.
 * Transitions reference these by id (e.g. `{ id: "frameAtRangeEnd" }`); xstate
 * resolves at machine build time via the `guards` registry passed to `setup()`.
 *
 * Each guard receives an xstate-style `{ context, event, params? }` arg. Param
 * shapes are defined per-guard via zod; invalid params evaluate as `false`
 * (guard does not fire). Catalog growth is incremental — v1 covers obvious
 * sprite use-cases.
 */

interface GuardArgs {
  // xstate v5 GuardPredicate arg shape — context and event are intentionally
  // unknown since user machines have arbitrary context/event types.
  context: unknown;
  event: unknown;
  params?: unknown;
}

type GuardFn = (args: GuardArgs) => boolean;

const FrameIsParamsSchema = z.object({ n: z.number().int().min(0) });
const RandomChanceParamsSchema = z.object({ p: z.number().min(0).max(1) });

const frameAtRangeStart: GuardFn = () => {
  const ctx = store.getSnapshot().context;
  const cur = ctx.activeFrame;
  for (const range of Object.values(ctx.stateFrames)) {
    if (cur === range[0]) return true;
  }
  return false;
};

const frameAtRangeEnd: GuardFn = () => {
  const ctx = store.getSnapshot().context;
  const cur = ctx.activeFrame;
  for (const range of Object.values(ctx.stateFrames)) {
    if (cur === range[1]) return true;
  }
  return false;
};

const frameIs: GuardFn = ({ params }) => {
  const r = FrameIsParamsSchema.safeParse(params);
  if (!r.success) return false;
  return store.getSnapshot().context.activeFrame === r.data.n;
};

const randomChance: GuardFn = ({ params }) => {
  const r = RandomChanceParamsSchema.safeParse(params);
  if (!r.success) return false;
  return Math.random() < r.data.p;
};

/**
 * Map of guard id → predicate. Passed to `setup({ guards })`; ids consumed by
 * `validate.ts` and the editor's catalog picker.
 */
export const guardCatalog: Record<string, GuardFn> = {
  frameAtRangeStart,
  frameAtRangeEnd,
  frameIs,
  randomChance,
};

export const guardCatalogIds: ReadonlySet<string> = new Set(Object.keys(guardCatalog));
